import { useTranslation } from 'react-i18next';
import { useNavigate, useRouteError, isRouteErrorResponse } from 'react-router-dom';
import { ROUTES } from '@/core/routes/constants';

interface ErrorPageProps {
  error?: Error | null;
  isProduction?: boolean;
}

export default function ErrorPage({ error: errorProp, isProduction }: ErrorPageProps) {
  const { t } = useTranslation();
  
  // useNavigate can fail outside of Router context
  let navigate: ReturnType<typeof useNavigate> | null = null;
  try {
    navigate = useNavigate();
  } catch {
    // Not inside Router context
  }
  
  // Try to get error from React Router first, then fall back to prop
  let routeError: unknown = null;
  try {
    routeError = useRouteError();
  } catch {
    // useRouteError can only be used inside an errorElement
    // If we're not in that context, it will throw, so we ignore it
  }

  // Determine the actual error to display
  let error: Error | null = errorProp || null;
  
  if (routeError) {
    // If it's a route error response, extract the error
    if (isRouteErrorResponse(routeError)) {
      // Route error responses have status and statusText
      error = new Error(routeError.statusText || `Error ${routeError.status}`);
    } else if (routeError instanceof Error) {
      error = routeError;
    } else {
      // Convert unknown error to Error
      error = new Error(String(routeError));
    }
  }

  // Detect production mode if not explicitly provided
  const isProd = isProduction ?? import.meta.env.PROD;

  // Check if it's a chunk loading error (dynamic import failure)
  const isChunkError = error?.message?.includes('Failed to fetch dynamically imported module') ||
    error?.message?.includes('Loading chunk') ||
    error?.name === 'ChunkLoadError';

  const handleRetry = () => {
    // Reload the page to retry loading
    window.location.reload();
  };

  const handleGoHome = () => {
    if (navigate) {
      navigate(ROUTES.HOME);
    } else {
      // Fallback when Router context is not available
      window.location.href = ROUTES.HOME;
    }
  };

  return (
    <div className="min-h-screen bg-secondary-50 dark:bg-secondary-900 flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white dark:bg-secondary-800 rounded-lg shadow-lg p-8 text-center">
        {/* Error Icon */}
        <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-4">
          <svg
            className="h-8 w-8 text-danger-600 dark:text-danger-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        </div>

        {/* Title */}
        <h1 className="text-2xl font-bold text-secondary-900 dark:text-white mb-2">
          {isProd
            ? t('error.title')
            : t('error.titleDev')}
        </h1>

        {/* Message */}
        <p className="text-secondary-600 dark:text-secondary-400 mb-6">
          {isProd
            ? (isChunkError
                ? t('error.chunkErrorProduction')
                : t('error.messageProduction'))
            : t('error.messageDev')}
        </p>

        {/* Error Details (only in development) */}
        {!isProd && error && (
          <div className="mb-6 p-4 bg-danger-50 dark:bg-danger-900/20 border border-red-200 rounded text-left">
            <p className="text-sm font-semibold text-red-800 mb-2">
              {t('error.details')}:
            </p>
            <p className="text-xs text-red-700 font-mono break-all">
              {error.message}
            </p>
            {error.stack && (
              <details className="mt-2">
                <summary className="text-xs text-danger-600 dark:text-danger-400 cursor-pointer">
                  {t('error.stackTrace')}
                </summary>
                <pre className="text-xs text-red-700 mt-2 overflow-auto max-h-40">
                  {error.stack}
                </pre>
              </details>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          {isChunkError ? (
            <button
              onClick={handleRetry}
              className="px-6 py-2 bg-primary-600 dark:bg-primary-500 text-white rounded-lg hover:bg-primary-700 dark:hover:bg-primary-600 transition-colors font-medium"
            >
              {t('error.retry')}
            </button>
          ) : (
            <button
              onClick={handleGoHome}
              className="px-6 py-2 bg-primary-600 dark:bg-primary-500 text-white rounded-lg hover:bg-primary-700 dark:hover:bg-primary-600 transition-colors font-medium"
            >
              {t('error.goHome')}
            </button>
          )}
          {isChunkError && (
            <button
              onClick={handleGoHome}
              className="px-6 py-2 bg-secondary-200 dark:bg-secondary-700 text-secondary-700 dark:text-secondary-300 rounded-lg hover:bg-secondary-300 dark:bg-secondary-600 transition-colors font-medium"
            >
              {t('error.goHome')}
            </button>
          )}
        </div>

        {/* Help Text */}
        {isProd && (
          <p className="mt-6 text-sm text-secondary-500 dark:text-secondary-400">
            {t('error.helpText')}
          </p>
        )}
      </div>
    </div>
  );
}

